import { Button, GoalStepper } from '@root/components'
import { useBusinessContext } from '@root/context'
import React from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FaCheck, FaMinusCircle } from 'react-icons/fa'
import { Goal, GoalButtons, GoalHeader, GoalText } from '../CancelFlow.styled'
import { CancelFlowFormValues } from '../types'

export const CancelOptionsStep: React.FC<{ activeAddonsCount: number }> = ({
  activeAddonsCount
}) => {
  const { t } = useTranslation()
  const { data } = useBusinessContext()
  const { control, setValue } = useFormContext<CancelFlowFormValues>()

  const disableEinvoicing = useWatch({ control, name: 'disableEinvoicing' })
  const keepAddons = useWatch({ control, name: 'keepAddons' })

  const hasStepsToComplete = data.einvoicing_enabled || activeAddonsCount > 0
  const isRemovingEinvoice = disableEinvoicing !== false
  const isKeepingEinvoice = disableEinvoicing !== true
  const hasSelectedEinvoiceOption = disableEinvoicing !== undefined
  const isKeepingAddons = keepAddons !== false
  const isRemovingAddons = keepAddons !== true
  const hasSelectedAddonsOption = keepAddons !== undefined

  return (
    <>
      {hasStepsToComplete ? (
        <p>{t('settings.billing.cancelIntro')}</p>
      ) : (
        <p>{t('settings.billing.cancelIntro2')}</p>
      )}
      <GoalStepper
        goals={[
          ...(data.einvoicing_enabled
            ? [
                {
                  content: (
                    <Goal>
                      <GoalHeader>{t('settings.billing.cancelEinvoiceTitle')}</GoalHeader>
                      <GoalText>{t('settings.billing.cancelEinvoiceText')}</GoalText>

                      <GoalButtons>
                        {isRemovingEinvoice && (
                          <Button
                            size="sm"
                            type="button"
                            isSecondary
                            intent="danger"
                            icon={<FaMinusCircle />}
                            onClick={() => setValue('disableEinvoicing', true)}
                            disabled={hasSelectedEinvoiceOption}
                          >
                            {hasSelectedEinvoiceOption
                              ? t('settings.billing.canceling')
                              : t('settings.billing.cancel')}
                          </Button>
                        )}
                        {isKeepingEinvoice && (
                          <Button
                            size="sm"
                            type="button"
                            isSecondary
                            intent="success"
                            icon={<FaCheck />}
                            onClick={() => setValue('disableEinvoicing', false)}
                            disabled={hasSelectedEinvoiceOption}
                          >
                            {hasSelectedEinvoiceOption
                              ? t('settings.billing.keeping')
                              : t('settings.billing.keep')}
                          </Button>
                        )}
                      </GoalButtons>
                    </Goal>
                  ),
                  isCompleted: disableEinvoicing !== undefined
                }
              ]
            : []),
          ...(activeAddonsCount > 0
            ? [
                {
                  content: (
                    <Goal>
                      <GoalHeader>{t('settings.billing.cancelAddonsTitle')}</GoalHeader>
                      <GoalText>{t('settings.billing.cancelAddonsText')}</GoalText>

                      <GoalButtons>
                        {isRemovingAddons && (
                          <Button
                            size="sm"
                            type="button"
                            isSecondary
                            intent="danger"
                            icon={<FaMinusCircle />}
                            onClick={() => setValue('keepAddons', false)}
                            disabled={hasSelectedAddonsOption}
                          >
                            {hasSelectedAddonsOption
                              ? t('settings.billing.canceling')
                              : t('settings.billing.cancelAddonsRemove')}
                          </Button>
                        )}
                        {isKeepingAddons && (
                          <Button
                            size="sm"
                            type="button"
                            isSecondary
                            intent="success"
                            icon={<FaCheck />}
                            onClick={() => setValue('keepAddons', true)}
                            disabled={hasSelectedAddonsOption}
                          >
                            {hasSelectedAddonsOption
                              ? t('settings.billing.keeping')
                              : t('settings.billing.cancelAddonsKeep')}
                          </Button>
                        )}
                      </GoalButtons>
                    </Goal>
                  ),
                  isCompleted: keepAddons !== undefined
                }
              ]
            : [])
        ]}
      />
    </>
  )
}
